import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { type ReactNode, useEffect } from 'react';
import { TouchableWithoutFeedback } from 'react-native';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    CircleAlertIcon,
    Icon,
    isWeb,
    PlusIcon,
    Separator,
    tokens,
    View,
    XStack,
    type XStackProps,
    YStack,
    type YStackProps,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import {
    getBillTitle,
    getCadenceDisplayString,
    getCategoryDisplayString,
} from '../../../common/utils/billUtils';
import { formatDateToOrdinal } from '../../../common/utils/dateUtils';
import { selectedBillAtom } from '../../state/BillsAtoms';
import {
    type _Bill,
    BillActivationStatus,
    type BillCategory,
    BillCategoryTextColors,
    BillTransactionStatus,
    type IBill,
    type PartialBill,
} from '../../types/Bill';
import { BillIcon, BillIconSize } from '../common/BillIcons';

interface BaseCardProps extends YStackProps {
    children: ReactNode;
}

const BaseListItem = ({ children, ...props }: BaseCardProps) => {
    return (
        <YStack
            padding={8}
            backgroundColor="$white"
            gap={2}
            animation="button"
            pressStyle={{
                scale: 0.95,
            }}
            {...props}
        >
            {children}
        </YStack>
    );
};

interface BillCardProps extends XStackProps {
    bill: IBill | PartialBill;
}

const getTransactionPaidForCurrentMonth = (
    bill: _Bill,
): { paidDay: string | null; paidAmountThisMonth: number | null } => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    if (!bill.transactions?.length) {
        return {
            paidAmountThisMonth: null,
            paidDay: null,
        };
    }

    const thisMonthTransactions = bill.transactions?.filter((transaction) => {
        const transactionDate = new Date(transaction.transactionDate);
        const transactionMonth = transactionDate.getMonth();
        const transactionYear = transactionDate.getFullYear();

        return (
            transaction.status === BillTransactionStatus.ACTIVE &&
            transactionMonth === currentMonth &&
            transactionYear === currentYear
        );
    });

    let totalPaid = 0;
    let latestTransactionOrdinal: string | null = null;
    thisMonthTransactions.forEach((transaction) => {
        totalPaid += transaction.transactionAmount;
        latestTransactionOrdinal = formatDateToOrdinal(
            transaction.transactionDate,
        );
    });

    return {
        paidAmountThisMonth: totalPaid,
        paidDay: latestTransactionOrdinal,
    };
};

const getBillUpdateDetails = (bill: _Bill) => {
    const { paidAmountThisMonth, paidDay } =
        getTransactionPaidForCurrentMonth(bill);
    const { activationStatus, cadence } = bill;

    if ('proposedChanges' in bill) {
        return (
            <BodySmallRegular color="$orange1100">
                Change Detected{' '}
                <Icon icon={CircleAlertIcon} color="$orange1000" size={14} />
            </BodySmallRegular>
        );
    } else if (activationStatus === BillActivationStatus.REVIEW) {
        return (
            <BodySmallRegular color="$orange1100">
                New bill detected{' '}
                <Icon icon={CircleAlertIcon} color="$orange1000" size={14} />
            </BodySmallRegular>
        );
    } else if (paidAmountThisMonth !== 0 && paidAmountThisMonth != null) {
        return (
            <BodySmallRegular color="$green1000">
                Paid {paidDay}
            </BodySmallRegular>
        );
    } else {
        return (
            <BodySmallRegular color="$gray1000">
                {getCadenceDisplayString(cadence)}
            </BodySmallRegular>
        );
    }
};

const BillListItem = ({ bill, ...props }: BillCardProps) => {
    const { icon, color, amount, category } = bill;
    const iconColor: string = BillCategoryTextColors[category as BillCategory];
    return (
        <BaseListItem {...props}>
            <XStack alignItems="center" gap={8} width="100%">
                <XStack width={BillIconSize.XS}>
                    <BillIcon
                        icon={icon}
                        color={color}
                        iconColor={iconColor}
                        size={BillIconSize.XS}
                    />
                </XStack>
                <YStack flex={1} justifyContent="center">
                    <BodyMediumEmphasized numberOfLines={1}>
                        {getBillTitle(bill)}
                    </BodyMediumEmphasized>
                    <BodySmallRegular color="$gray1000">
                        {getCategoryDisplayString(category ?? '')}
                    </BodySmallRegular>
                </YStack>
                <YStack alignItems="flex-end" justifyContent="center">
                    <BodyMediumEmphasized color="black">
                        {formatDollars(amount, false)}
                    </BodyMediumEmphasized>
                    {getBillUpdateDetails(bill as _Bill)}
                </YStack>
            </XStack>
        </BaseListItem>
    );
};

interface BillCardButtonProps extends BillCardProps {
    showBillDetails: (bill: IBill | PartialBill) => void;
}

const BillCardButton = ({
    showBillDetails,
    bill,
    ...props
}: BillCardButtonProps) => {
    return (
        <TouchableWithoutFeedback
            onPress={() => {
                showBillDetails(bill);
            }}
        >
            <BillListItem bill={bill} {...props} />
        </TouchableWithoutFeedback>
    );
};

interface AddBillCardButtonProps extends YStackProps {
    onPress: () => void;
}

const AddBillCardButton = ({ onPress, ...props }: AddBillCardButtonProps) => {
    return (
        <TouchableWithoutFeedback onPress={onPress}>
            <BaseListItem
                alignItems="center"
                justifyContent="center"
                {...props}
            >
                <View
                    width="100%"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                >
                    <View
                        borderRadius={100}
                        backgroundColor={tokens.color.light.gray.gray100}
                        width={48}
                        height={48}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon icon={PlusIcon} color="$black" />
                    </View>
                </View>
            </BaseListItem>
        </TouchableWithoutFeedback>
    );
};

interface BillCardsProps {
    bills: IBill[];
    showBillDetails: (bill: IBill | PartialBill) => void;
}

export const BillList = ({ bills, showBillDetails }: BillCardsProps) => {
    const setSelectedBill = useSetAtom(selectedBillAtom);

    useEffect(() => {
        setSelectedBill(null);
    }, [setSelectedBill]);

    return (
        <YStack
            gap={8}
            paddingHorizontal={isWeb ? 20 : undefined}
            separator={<Separator />}
        >
            {bills.map((b, i) => (
                <BillCardButton
                    width="100%"
                    bill={b}
                    key={i}
                    showBillDetails={showBillDetails}
                />
            ))}
            <AddBillCardButton
                width="100%"
                paddingBottom={isWeb ? 24 : undefined}
                onPress={() => {
                    router.push('/bills/bill-add-new');
                }}
            />
        </YStack>
    );
};
